import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { useAccount } from "../contexts/AccountContext";
import { Home as HomeIcon, Sparkles, Mail, MessageCircle, UserCircle, Trophy, LogOut, Menu, X, ChevronDown, Share2 } from "lucide-react";
import Countdown from "./Countdown";
import HeaderLogoRing, { ringConfigFromSettings } from "./HeaderLogoRing";
import { RoundFlag } from "./RoundFlag";
import RichText from "./RichText";

export default function Header() {
  const { lang, chooseLang, t, settings } = useApp();
  const { account } = useAccount();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const isHome = location.pathname === "/";
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const autohide = !!settings.header_autohide;
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [ptOpen, setPtOpen] = useState(false);

  useEffect(() => {
    if (!ptOpen) return;
    const close = () => setPtOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [ptOpen]);
  const hasAdmin = typeof window !== "undefined" && !!localStorage.getItem("admin_token");

  const adminLogout = () => { localStorage.removeItem("admin_token"); window.location.href = "/"; };

  useEffect(() => {
    if (!autohide) { setHidden(false); return; }
    const getY = () => window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    let last = getY();
    const onScroll = () => {
      const y = getY();
      if (y > last && y > 120) setHidden(true);
      else if (y < last) setHidden(false);
      last = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, [autohide]);

  const logoPath = lang === "en" ? settings.logo_image_path_en : settings.logo_image_path_pt;
  const logoUrl = logoPath && settings.show_logo_image !== false ? `${backendUrl}/api/files/${logoPath}` : null;
  const headerLogoPath = lang === "en" ? settings.header_logo_path_en : settings.header_logo_path_pt;
  const headerLogoUrl = headerLogoPath && settings.show_logo_image !== false ? `${backendUrl}/api/files/${headerLogoPath}` : null;
  const extraText = lang === "en" ? settings.header_extra_text_en : settings.header_extra_text_pt;
  const brandTitle = lang === "en" ? settings.header_brand_title_en : settings.header_brand_title_pt;
  const brandSubtitle = lang === "en" ? settings.header_brand_subtitle_en : settings.header_brand_subtitle_pt;
  const brandUrl = settings.header_brand_url || "";
  const ticker = lang === "en" ? settings.header_ticker_en : settings.header_ticker_pt;
  const tickerOn = !!settings.header_ticker_enabled && !!ticker;

  if (isAdmin) return null;
  if (settings.header_enabled === false) return null;

  return (
    <div className={`sticky top-0 z-40 transition-transform duration-300 ${hidden ? "-translate-y-full" : "translate-y-0"}`} data-testid="site-header-wrap">
      {tickerOn && (
        <div className="relative z-30 overflow-hidden" data-testid="header-ticker"
          style={{ background: settings.header_ticker_bg || "#0B1D3A", color: settings.header_ticker_color || "#C8A24B" }}>
          <div className="ticker-track py-2 font-semibold whitespace-nowrap"
            style={{ fontFamily: settings.header_ticker_font || "Nunito", fontSize: `${Number(settings.header_ticker_size) || 16}px`, animationDuration: `${Number(settings.header_ticker_speed) || 40}s` }}>
            <span className="px-12">★ {ticker} ★ {ticker} ★ {ticker} ★ {ticker}</span>
          </div>
          <style>{`
            .ticker-track { display:inline-block; animation: ticker-scroll 40s linear infinite; }
            @keyframes ticker-scroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
          `}</style>
        </div>
      )}
      <header className="px-3 sm:px-6 md:px-10 pt-6 pb-3 flex items-center justify-between gap-4 backdrop-blur-md bg-white/70" data-testid="site-header">
        <div className="flex items-center gap-3">
          {(() => {
            const link = (settings.header_logo_link || "").trim();
            const isExternal = /^https?:\/\//i.test(link);
            const logoInner = headerLogoUrl ? (
              <HeaderLogoRing src={headerLogoUrl} {...ringConfigFromSettings(settings)} />
            ) : (
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1B5E3A] shadow-[0_8px_18px_rgba(0,0,0,0.18)]">
                <Sparkles className="text-white" strokeWidth={2.5} />
              </span>
            );
            if (isExternal) {
              return <a href={link} target="_blank" rel="noreferrer" className="flex items-center group shrink-0" data-testid="header-logo">{logoInner}</a>;
            }
            return <Link to={link || "/"} className="flex items-center group shrink-0" data-testid="header-logo">{logoInner}</Link>;
          })()}
          {(brandTitle || brandSubtitle) && (() => {
            const inner = (
              <>
                {brandTitle && (
                  <span className="font-fredoka text-base md:text-xl font-bold text-[#0B1D3A] tracking-wide group-hover/brand:text-[#C1121F] transition-colors">
                    {brandTitle}
                  </span>
                )}
                {brandSubtitle && (
                  <span className="font-nunito text-[10px] md:text-xs font-semibold text-[#0B1D3A]/60 tracking-[0.22em] mt-0.5">
                    {brandSubtitle}
                  </span>
                )}
              </>
            );
            const cls = "hidden sm:flex flex-col justify-center leading-none";
            return brandUrl ? (
              <a href={brandUrl} target="_blank" rel="noreferrer" className={`group/brand ${cls} hover:opacity-90`} data-testid="header-brand" title={brandTitle || ""}>
                {inner}
              </a>
            ) : (
              <span className={`group/brand ${cls}`} data-testid="header-brand">
                {inner}
              </span>
            );
          })()}
          {!isHome && settings.show_logo_text !== false && (
            <Link to="/" data-testid="header-app-name" className="font-fredoka text-xl md:text-2xl lg:text-3xl font-bold text-[#0B1D3A] tracking-tight whitespace-nowrap pl-3 border-l border-[#0B1D3A]/15">
              <RichText k="app_name" fallback={lang !== "en" ? "NATALMÁTICO" : "X-MATHS Challenge"} />
            </Link>
          )}
          {extraText && (
            <span className="hidden md:inline-block font-nunito text-sm font-semibold text-[#0B1D3A]/70 ml-2 max-w-xs truncate">
              {extraText}
            </span>
          )}
        </div>
        {settings.countdown_position === "right_of_logo" && (
          <div className="hidden md:block flex-shrink-0" data-testid="countdown-right-of-logo">
            <Countdown />
          </div>
        )}
        <div className="flex items-center gap-1 md:gap-2 ml-auto min-w-0" data-testid="header-nav">
          {settings.contact_email_enabled && (lang === "en" ? settings.contact_email_en : settings.contact_email) && (
            <a
              href={`mailto:${lang === "en" ? settings.contact_email_en : settings.contact_email}`}
              className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border-2 border-[#0B1D3A]/10 text-[#0B1D3A] hover:border-[#C1121F] hover:text-[#C1121F] transition"
              title={lang !== "en" ? "Enviar email" : "Send email"} data-testid="header-email-btn" aria-label="email">
              <Mail size={18} />
            </a>
          )}
          {settings.contact_whatsapp_enabled && (lang === "en" ? settings.contact_whatsapp_en : settings.contact_whatsapp) && (
            <a
              href={`https://wa.me/${(lang === "en" ? settings.contact_whatsapp_en : settings.contact_whatsapp).replace(/[^0-9]/g, "")}?text=${encodeURIComponent((lang === "en" ? settings.whatsapp_default_message_en : settings.whatsapp_default_message_pt) || "")}`}
              target="_blank" rel="noreferrer"
              className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366] text-white hover:bg-[#1FAE54] transition"
              title="WhatsApp" data-testid="header-whatsapp-btn" aria-label="whatsapp">
              <MessageCircle size={18} />
            </a>
          )}

          {/* Nav tabs (icon + label) */}
          <button onClick={() => navigate("/")} className="hidden sm:flex flex-col items-center justify-center gap-0.5 px-2.5 group" data-testid="header-home-btn" title={lang !== "en" ? "Início" : "Home"}>
            <span className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${isHome ? "bg-[#1B5E3A] text-white" : "bg-[#0B1D3A]/5 text-[#0B1D3A] group-hover:bg-[#1B5E3A] group-hover:text-white"}`}><HomeIcon size={18} /></span>
            <span className="text-[10px] font-bold uppercase tracking-wide text-[#0B1D3A]/70 group-hover:text-[#0B1D3A]">{lang !== "en" ? "Início" : "Home"}</span>
          </button>
          <button onClick={() => navigate("/results")} className="hidden sm:flex flex-col items-center justify-center gap-0.5 px-2.5 group" data-testid="header-ranking-btn" title="Ranking">
            <span className={`h-9 w-9 rounded-full flex items-center justify-center transition-colors ${location.pathname === "/results" ? "bg-[#1B5E3A] text-white" : "bg-[#0B1D3A]/5 text-[#0B1D3A] group-hover:bg-[#C8A24B] group-hover:text-[#0B1D3A]"}`}><Trophy size={18} /></span>
            <span className="text-[10px] font-bold uppercase tracking-wide text-[#0B1D3A]/70 group-hover:text-[#0B1D3A]">Ranking</span>
          </button>

          {settings.share_button_enabled !== false && (settings.share_button_url || "").trim() && (
            <a href={settings.share_button_url} target="_blank" rel="noreferrer" className="hidden sm:flex flex-col items-center justify-center gap-0.5 px-2.5 group" data-testid="header-share-btn" title={settings.share_button_label || "Partilhar"}>
              <span className="h-9 w-9 rounded-full flex items-center justify-center bg-[#0B1D3A]/5 text-[#0B1D3A] group-hover:bg-[#7C3AED] group-hover:text-white transition-colors"><Share2 size={18} /></span>
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#0B1D3A]/70 group-hover:text-[#0B1D3A] max-w-[72px] truncate">{settings.share_button_label || "Partilhar"}</span>
            </a>
          )}

          {(account ||
            settings.account_email_enabled !== false ||
            (!!process.env.REACT_APP_GOOGLE_CLIENT_ID && settings.account_google_enabled !== false) ||
            (!!process.env.REACT_APP_FACEBOOK_APP_ID && settings.account_facebook_enabled !== false)) && (
            <button onClick={() => navigate(account ? "/account/dashboard" : "/account")} className="hidden sm:flex flex-col items-center justify-center gap-0.5 px-2.5 group" data-testid="header-account-btn" title={lang !== "en" ? "Conta" : "Account"}>
              <span className="h-9 w-9 rounded-full flex items-center justify-center bg-[#0B1D3A]/5 text-[#0B1D3A] group-hover:bg-[#C1121F] group-hover:text-white transition-colors"><UserCircle size={18} /></span>
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#0B1D3A]/70 group-hover:text-[#0B1D3A]">{lang !== "en" ? "Conta" : "Account"}</span>
            </button>
          )}

          <span className="hidden sm:block h-9 w-px bg-[#0B1D3A]/10 mx-1" />

          {/* Language pills (configurable) */}
          {(() => {
            const showLabels = settings.header_lang_show_labels !== false;
            const base = "h-9 px-2.5 rounded-full text-xs font-extrabold uppercase transition-colors flex items-center gap-1.5";
            const cls = (active) => `${base} ${active ? "bg-[#1B5E3A] text-white" : "bg-white border-2 border-[#0B1D3A]/15 text-[#0B1D3A]/70 hover:border-[#1B5E3A] hover:text-[#1B5E3A]"}`;
            const hasFlag = (l) => settings[`header_lang_${l}_flag`] !== false;
            const ptVariants = [
              { l: "pt", flag: "eu", name: "Português (Europa)" },
              { l: "br", flag: "br", name: "Português (Brasil)" },
            ].filter((v) => settings[`header_lang_${v.l}_enabled`] !== false);
            const enOn = settings.header_lang_en_enabled !== false;
            if (ptVariants.length === 0 && !enOn) return null;
            const ptActive = lang === "pt" || lang === "br";
            const current = ptVariants.find((v) => v.l === lang) || ptVariants[0];
            const ptLabel = settings.header_lang_pt_label ?? "PT";
            return (
              <div className="flex items-center gap-1" data-testid="language-switcher">
                {ptVariants.length === 1 && (
                  <button onClick={() => chooseLang(ptVariants[0].l)} data-testid={`lang-${ptVariants[0].l}`} className={cls(ptActive)}>
                    {hasFlag(ptVariants[0].l) && <RoundFlag code={ptVariants[0].flag} size={18} />} {showLabels && ptLabel}
                  </button>
                )}
                {ptVariants.length >= 2 && (
                  <div className="relative">
                    <button type="button" onClick={(e) => { e.stopPropagation(); setPtOpen((o) => !o); }} data-testid="lang-pt-combo" aria-haspopup="listbox" aria-expanded={ptOpen} className={cls(ptActive)}>
                      {current && hasFlag(current.l) && <RoundFlag code={current.flag} size={18} />} {showLabels && ptLabel}
                      <ChevronDown size={14} className={`transition-transform ${ptOpen ? "rotate-180" : ""}`} />
                    </button>
                    {ptOpen && (
                      <div className="absolute right-0 mt-1.5 min-w-[190px] rounded-xl bg-white border border-[#0B1D3A]/10 shadow-xl overflow-hidden z-50" role="listbox" data-testid="lang-pt-menu">
                        {ptVariants.map((v) => (
                          <button key={v.l} type="button" role="option" aria-selected={lang === v.l}
                            onClick={(e) => { e.stopPropagation(); chooseLang(v.l); setPtOpen(false); }} data-testid={`lang-${v.l}`}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold transition-colors ${lang === v.l ? "bg-[#1B5E3A]/10 text-[#1B5E3A]" : "text-[#0B1D3A]/75 hover:bg-[#0B1D3A]/5"}`}>
                            <RoundFlag code={v.flag} size={20} /> {v.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {enOn && (
                  <button onClick={() => chooseLang("en")} data-testid="lang-en" className={cls(lang === "en")}>
                    {settings.header_lang_en_flag === true && <RoundFlag code="gb" size={18} />} {showLabels && (settings.header_lang_en_label ?? "EN")}
                  </button>
                )}
              </div>
            );
          })()}

          {hasAdmin && (
            <>
              <span className="h-9 w-px bg-[#0B1D3A]/10 mx-1" />
              <button onClick={adminLogout} className="hidden sm:flex flex-col items-center justify-center gap-0.5 px-2.5 group" data-testid="header-logout-btn" title={lang !== "en" ? "Sair" : "Sign out"}>
                <span className="h-9 w-9 rounded-full flex items-center justify-center bg-[#0B1D3A]/5 text-[#0B1D3A] group-hover:bg-[#C1121F] group-hover:text-white transition-colors"><LogOut size={18} /></span>
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#0B1D3A]/70 group-hover:text-[#0B1D3A]">{lang !== "en" ? "Sair" : "Out"}</span>
              </button>
            </>
          )}

          <button onClick={() => setMenuOpen((v) => !v)} className="sm:hidden inline-flex shrink-0 items-center justify-center w-10 h-10 rounded-full bg-[#0B1D3A] text-white ml-1" data-testid="header-mobile-menu-btn" aria-label="menu">
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>
      {menuOpen && (
        <div className="sm:hidden bg-white/95 backdrop-blur-md border-t border-[#0B1D3A]/10 px-6 py-3 space-y-1" data-testid="header-mobile-menu">
          {settings.contact_email_enabled && (lang === "en" ? settings.contact_email_en : settings.contact_email) && (
            <a href={`mailto:${lang === "en" ? settings.contact_email_en : settings.contact_email}`} onClick={() => setMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#0B1D3A] font-nunito font-semibold hover:bg-[#0B1D3A]/5" data-testid="m-email">
              <Mail size={18} /> {lang !== "en" ? "Enviar email" : "Send email"}
            </a>
          )}
          {settings.contact_whatsapp_enabled && (lang === "en" ? settings.contact_whatsapp_en : settings.contact_whatsapp) && (
            <a href={`https://wa.me/${(lang === "en" ? settings.contact_whatsapp_en : settings.contact_whatsapp).replace(/[^0-9]/g, "")}?text=${encodeURIComponent((lang === "en" ? settings.whatsapp_default_message_en : settings.whatsapp_default_message_pt) || "")}`} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#0B1D3A] font-nunito font-semibold hover:bg-[#0B1D3A]/5" data-testid="m-whatsapp">
              <MessageCircle size={18} /> WhatsApp
            </a>
          )}
          <button onClick={() => { setMenuOpen(false); navigate("/"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#0B1D3A] font-nunito font-semibold hover:bg-[#0B1D3A]/5" data-testid="m-home">
            <HomeIcon size={18} /> {lang !== "en" ? "Início" : "Home"}
          </button>
          <button onClick={() => { setMenuOpen(false); navigate("/results"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#0B1D3A] font-nunito font-semibold hover:bg-[#0B1D3A]/5" data-testid="m-ranking">
            <Trophy size={18} /> Ranking
          </button>
          {settings.share_button_enabled !== false && (settings.share_button_url || "").trim() && (
            <a href={settings.share_button_url} target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#0B1D3A] font-nunito font-semibold hover:bg-[#0B1D3A]/5" data-testid="m-share">
              <Share2 size={18} /> {settings.share_button_label || "Partilhar"}
            </a>
          )}
          {(account || settings.account_email_enabled !== false || (!!process.env.REACT_APP_GOOGLE_CLIENT_ID && settings.account_google_enabled !== false) || (!!process.env.REACT_APP_FACEBOOK_APP_ID && settings.account_facebook_enabled !== false)) && (
            <button onClick={() => { setMenuOpen(false); navigate(account ? "/account/dashboard" : "/account"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#0B1D3A] font-nunito font-semibold hover:bg-[#0B1D3A]/5" data-testid="m-account">
              <UserCircle size={18} /> {lang !== "en" ? "Conta" : "Account"}
            </button>
          )}
          {hasAdmin && (
            <>
              <button onClick={() => { setMenuOpen(false); adminLogout(); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#C1121F] font-nunito font-semibold hover:bg-[#C1121F]/5" data-testid="m-logout">
                <LogOut size={18} /> {lang !== "en" ? "Sair" : "Sign out"}
              </button>
            </>
          )}
        </div>
      )}
      {settings.countdown_position === "header_below_buttons" && (
        <div className="relative z-30 px-6 md:px-10 pb-3 flex justify-end" data-testid="countdown-header-below-buttons">
          <Countdown />
        </div>
      )}
    </div>
  );
}
